import { Link } from 'react-router-dom'

import { ChevronDown20Regular } from '@fluentui/react-icons'
import * as Accordion from '@radix-ui/react-accordion'
import { styled, keyframes } from '@styles/theme'

export const AccordionRoot = styled(Accordion.Root, {
	marginBottom: 16,
	borderRadius: 12,
	width: '100%',
	backgroundColor: '$neutral10',
	border: '1px solid $neutral50',
})

export const AccordionItem = styled(Accordion.Item, {
	overflow: 'hidden',
	marginTop: 1,

	'&:first-child': {
		marginTop: 0,
		borderTopLeftRadius: 12,
		borderTopRightRadius: 12,
	},

	'&:last-child': {
		borderBottomLeftRadius: 12,
		borderBottomRightRadius: 12,
	},
	variants: {
		border: {
			true: {},
		},
	},
})

export const StyledHeader = styled(Accordion.Header, {
	all: 'unset',
	display: 'flex',
})

export const StyledTrigger = styled(Accordion.Trigger, {
	all: 'unset',
	fontFamily: 'inherit',
	backgroundColor: 'transparent',
	padding: '16px',
	flex: 1,
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'space-between',
	lineHeight: 1,
	fontSize: '$p',
	color: '$neutral90',
	background: '$neutral20',
	position: 'relative',
	'& p': { position: 'absolute', right: 74 },
	'@md': {
		fontSize: '$lg',
	},

	variants: {
		paddingLeft: {
			true: {
				minHeight: 'auto',
				padding: '16px',
				'@md': {
					paddingLeft: 60,
				},
			},
		},
		isOpen: {
			true: {
				'h6.title': {
					maxHeight: 'unset',
					'-webkit-line-clamp': 'unset',
				},

				p: {
					color: '#29A0D2',
				},
			},
		},
	},
})

export const StyledNumber = styled('div', {
	fontFamily: 'Roboto',
	fontStyle: 'normal',
	fontWeight: 400,
	fontSize: '12px',
	lineHeight: '21px',
	display: 'flex',
	alignItems: 'center',
	letterSpacing: '0.05em',

	p: {
		display: 'none',
	},

	'@md': {
		p: {
			display: 'block',
		},
	},
})

export const StyledChevron = styled(ChevronDown20Regular, {
	width: '25px',
	height: '25px',
	color: '$neutral100',
	transition: 'transform 300ms cubic-bezier(0.87, 0, 0.13, 1)',
	variants: {
		isOpen: {
			true: {
				color: '#29A0D2',
				'[data-state=open] &': { transform: 'rotate(180deg)' },
			},
		},
	},
})

const slideDown = keyframes({
	from: { height: 0 },
	to: { height: 'var(--radix-accordion-content-height)' },
})

const slideUp = keyframes({
	from: { height: 'var(--radix-accordion-content-height)' },
	to: { height: 0 },
})

export const StyledContent = styled(Accordion.Content, {
	overflow: 'hidden',
	fontSize: '$lg',
	color: '$neutral90',
	background: '$neutral20',
	borderTop: '1px solid $neutral50',

	'&[data-state="open"]': {
		animation: `${slideDown} 300ms cubic-bezier(0.87, 0, 0.13, 1)`,
	},
	'&[data-state="closed"]': {
		animation: `${slideUp} 300ms cubic-bezier(0.87, 0, 0.13, 1)`,
	},
})

export const StyledContentText = styled('span', {
	position: 'relative',
	fontSize: '$p',
	'& p': { position: 'absolute', right: 74 },
	variants: {
		sub: {
			true: {
				display: 'flex',
				padding: '16px',
				lineHeight: '24px',
				'@md': {
					fontSize: '$lg',
					padding: '0px 101px',
					height: '58px',
					display: 'flex',
					alignItems: 'center',
				},
				'& p': { position: 'absolute', right: 20 },
				'& a': { maxWidth: '100%' },
			},
		},
	},
})

export const StyledLink = styled(Link, {
	cursor: 'pointer',
	textUnderlineOffset: 4,
	maxWidth: '85%',
	lineHeight: '24px',
	display: 'flex',
	gap: '16px',

	h6: {
		_font: 'normal',
		fontWeight: '700',
		fontSize: '$lg',
		lineHeight: '19px',
		color: '$neutral100',
	},

	'h6.title': {
		position: 'relative',
		display: '-webkit-box',
		'-webkit-box-orient': 'vertical',
		'-webkit-line-clamp': '2',
		overflow: 'hidden',
		maxHeight: 'calc(1.5em * 2)',
	},

	'@md': {
		maxWidth: '100%',
	},

	variants: {
		sub: {
			true: {
				h6: {
					fontWeight: '400',
				},
			},
		},
		isOpen: {
			true: {
				h6: {
					color: '#29A0D2',
				},
				p: {
					color: '#29A0D2',
				},
			},
		},
	},
})
